/* lib/adapters/settings.js — `profileReal` + `teamReal`: account-настройки поверх боевых клиентов.
 * Всё через unwrap → throw{status,code,detail}, т.к. экраны ловят e.code в catch-ветках, а TeamTab
 * читает через useFakeQuery (read-seam). Адаптеры скрывают расхождения backend↔mock:
 *
 *  • Профиль/email/пароль: бэк сериализатор смены email ждёт {new_email, password}; ProfileTab
 *    шлёт {new_email, current_password} → маппим. Коды бэка (same_email/email_taken/invalid_password/
 *    rate_limited) совпадают с тем, что ждёт ProfileTab.
 *  • Команда — is_admin (bool) ↔ role-строка ('member'/'admin') + ярлык role_name. Бэк НЕ отдаёт
 *    role-строку и initials → деривим. can_manage НЕ приходит из тела: успех GET = юзер админ (бэк 403
 *    не-админу), поэтому can_manage=true на успехе.
 *  • Роли доступа (C1): бэк-поле member.role = {id,name}|null → у нас assigned_role (строка role
 *    'member'/'admin' сохранена как раньше). role_name-ярлык: owner → «Владелец»; не-админ с назначенной
 *    ролью → имя роли; иначе прежний ярлык. permission_overrides — карта {key:bool} только
 *    не-«наследовать» ключей (true=разрешить, false=запретить); grants роли — только false-ключи.
 *    Чистые хелперы матрицы (grantsToMatrix/matrixToGrants/overridesFromTriState/…) живут здесь же —
 *    их зовут RoleEditorModal/EditMemberModal, тесты — settings.test.js.
 *  • Remap кодов: бэк 409 already_member → код 'exists' (его ждёт InviteMemberModal). invalid_email→'email'.
 *  • Avatar upload/delete: multipart POST/DELETE /api/auth/profile/avatar/ → обновлённый user (с avatar_url).
 *    ProfileTab после загрузки зовёт refreshUser(), чтобы аватар обновился во всей сессии. */

import { unwrap } from '../api/unwrap.js';
import * as profileApi from '../api/profile.js';
import * as teamApi from '../api/team.js';
import { ACCESS_SECTIONS, ACCESS_TABS, accessKey } from '../analyticsAccess.js';

// ── Профиль / Безопасность (user-level) ───────────────────────────────────

// Инициалы из ФИО (бэк member/user их не присылает; зеркалит AuthProvider.deriveInitials).
function deriveInitials(fullName, email) {
  const src = (fullName || '').trim();
  if (src) {
    const parts = src.split(/\s+/).filter(Boolean);
    const a = parts[0] ? parts[0][0] : '';
    const b = parts.length > 1 ? parts[parts.length - 1][0] : '';
    return (a + b).toUpperCase();
  }
  return (email || '?').slice(0, 1).toUpperCase();
}

// PATCH /api/auth/profile/ {full_name?, phone?} → user. ProfileTab сам синкает useAuth.user.
// Шлём только присланные ключи: бэк partial-апдейт (W3) терпит отсутствие любого поля,
// телефон опционален (W2b — есть в /me + profile-контракте, не переименовывать).
async function updateProfile(payload) {
  const body = {};
  if (payload && payload.full_name != null) body.full_name = payload.full_name;
  if (payload && payload.phone != null) body.phone = payload.phone;
  return unwrap(profileApi.updateProfile(body));
}

// POST/DELETE avatar → обновлённый user (avatar_url). ProfileTab затем зовёт refreshUser().
// Бэк-валидация (размер/формат) приходит как 400 detail → unwrap бросит {detail}.
async function uploadAvatar(file) {
  return unwrap(profileApi.uploadAvatar(file));
}

async function deleteAvatar() {
  return unwrap(profileApi.deleteAvatar());
}

// POST email/change/ — ProfileTab шлёт {new_email, current_password}; бэк ждёт {new_email, password}.
// Возвращает {pending_email}. Коды ошибок (same_email/email_taken/invalid_password/rate_limited) — как есть.
async function requestEmailChange(payload) {
  const body = {
    new_email: (payload && payload.new_email) || '',
    password: (payload && (payload.current_password ?? payload.password)) || '',
  };
  return unwrap(profileApi.requestEmailChange(body));
}

// POST email/change/cancel/ → user (pending_email сброшен). ProfileTab трактует успех как «отменено».
async function cancelEmailChange() {
  return unwrap(profileApi.cancelEmailChange());
}

// POST password/change/ {current_password, new_password} → {ok}. invalid_password / rate_limited.
async function changePassword(payload) {
  return unwrap(profileApi.changePassword({
    current_password: (payload && payload.current_password) || '',
    new_password: (payload && payload.new_password) || '',
  }));
}

// POST sessions/revoke-all/ → {revoked}. SecurityTab показывает счётчик.
async function revokeOtherSessions() {
  return unwrap(profileApi.revokeOtherSessions());
}

// POST account/delete/ {password} → {ok}. После — экран делает logout/редирект на /login.
async function deleteAccount(payload) {
  return unwrap(profileApi.deleteAccount({ password: (payload && payload.password) || '' }));
}

export const profileReal = {
  updateProfile, uploadAvatar, deleteAvatar, requestEmailChange, cancelEmailChange,
  changePassword, revokeOtherSessions, deleteAccount,
};

// ── Команда (workspace-scoped) ─────────────────────────────────────────────

// is_admin (bool) → role-строка для MemberRow/EditMemberModal (owner > admin > member).
function memberRole(m) {
  if (m.is_owner) return 'owner';
  return m.is_admin ? 'admin' : 'member';
}

// Ярлык роли строкой (бэк отдаёт role_name свободной строкой; если пусто — дефолт по флагу).
function roleLabel(roleName, isAdmin) {
  if (roleName) return roleName;
  return isAdmin ? 'Администратор' : 'Участник';
}

// Бэк-поле role (роль доступа C1) = {id,name}|null. null-безопасно: старый бэк поля не шлёт.
function adaptAssignedRole(role) {
  return role && typeof role === 'object' && role.id != null
    ? { id: role.id, name: role.name || '' }
    : null;
}

function adaptMember(m) {
  const assigned = adaptAssignedRole(m.role);
  return {
    id: m.id,
    user_id: m.user_id,
    full_name: m.full_name || '',
    email: m.email || '',
    avatar_url: m.avatar_url || null,
    initials: deriveInitials(m.full_name, m.email),
    is_owner: !!m.is_owner,
    is_admin: !!m.is_admin,
    role: memberRole(m),
    // владелец одновременно is_admin=true, поэтому ярлык переопределяем явно (иначе «Администратор»);
    // у НЕ-админа с назначенной ролью доступа показываем ЕЁ имя (админ видит всё — роль на нём не действует).
    role_name: m.is_owner ? 'Владелец'
      : (!m.is_admin && assigned && assigned.name) ? assigned.name
      : roleLabel(m.role_name, m.is_admin),
    assigned_role: assigned,
    permission_overrides: m.permission_overrides || {},
  };
}

function adaptInvitation(inv) {
  const assigned = adaptAssignedRole(inv.role);
  return {
    id: inv.id,
    email: inv.email || '',
    is_admin: !!inv.is_admin,
    role: inv.is_admin ? 'admin' : 'member',
    // как у участника: не-админ с назначенной ролью → имя роли вместо ярлыка статуса.
    role_name: (!inv.is_admin && assigned && assigned.name) ? assigned.name
      : roleLabel(inv.role_name, inv.is_admin),
    assigned_role: assigned,
    is_expired: !!inv.is_expired,
    expires_at: inv.expires_at,
    token: inv.token,
  };
}

// ── Роли доступа: чистые хелперы матрицы (тесты — settings.test.js) ────────

// Число ограниченных ключей роли (false-ключи grants) — строка «Ограничений: N» в списке ролей.
export function countRestrictedKeys(grants) {
  return Object.values(grants || {}).filter((v) => v === false).length;
}

// grants роли → состояние матрицы UI {tabs:{tab:bool}, sections:{tab:{sec:bool}}}.
// true = разрешено; дефолт «всё включено» (отсутствие ключа = разрешено, как в analyticsAccess).
export function grantsToMatrix(grants) {
  const g = grants || {};
  const tabs = {};
  const sections = {};
  for (const t of ACCESS_TABS) {
    tabs[t.key] = g[accessKey(t.key)] !== false;
    sections[t.key] = {};
    for (const s of ACCESS_SECTIONS[t.key] || []) {
      sections[t.key][s.key] = g[accessKey(t.key, s.key)] !== false;
    }
  }
  return { tabs, sections };
}

// Матрица UI → grants: пишем ТОЛЬКО false-ключи (снятые галки), true не отправляем.
// Выключенная вкладка сериализуется одним ключом вкладки — её секции схлопнуты
// (запрет вкладки и так закрывает секции; при повторном включении они вернутся разрешёнными).
export function matrixToGrants(matrix) {
  const grants = {};
  const m = matrix || { tabs: {}, sections: {} };
  for (const t of ACCESS_TABS) {
    if (m.tabs && m.tabs[t.key] === false) {
      grants[accessKey(t.key)] = false;
      continue;
    }
    const secs = (m.sections && m.sections[t.key]) || {};
    for (const s of ACCESS_SECTIONS[t.key] || []) {
      if (secs[s.key] === false) grants[accessKey(t.key, s.key)] = false;
    }
  }
  return grants;
}

// Тристейт исключений участника {key:'inherit'|'allow'|'deny'} → {key:bool}:
// только не-«наследовать» ключи (true=разрешить, false=запретить); всё inherit → {} (очистка).
export function overridesFromTriState(state) {
  const out = {};
  for (const [k, v] of Object.entries(state || {})) {
    if (v === 'allow') out[k] = true;
    else if (v === 'deny') out[k] = false;
  }
  return out;
}

// Обратный маппинг для инициализации формы: {key:bool} → {key:'allow'|'deny'} (остальное — inherit).
export function triStateFromOverrides(overrides) {
  const out = {};
  for (const [k, v] of Object.entries(overrides || {})) out[k] = v === false ? 'deny' : 'allow';
  return out;
}

// Полная замена ({} очищает) — сравниваем карты исключений по ключам и значениям.
function sameOverrides(a = {}, b = {}) {
  const ka = Object.keys(a); const kb = Object.keys(b);
  return ka.length === kb.length && ka.every((k) => a[k] === b[k]);
}

// Сборка частичного patch-payload участника из состояния формы EditMemberModal
// (D11, omission-семантика — шлём ТОЛЬКО изменённые поля):
//   role — при явной смене статуса; role_id (int|null) — при смене роли доступа
//   ('' = «Без роли» → null); permission_overrides — ПОЛНАЯ замена при изменении
//   ({} очищает). Для администратора role_id/overrides не шлём (скрыты, не действуют).
export function buildMemberPatch(member, { role, roleId, overrides }) {
  const m = member || {};
  const patch = {};
  if (role !== m.role) patch.role = role;
  if (role !== 'admin') {
    const initialRoleId = m.assigned_role ? String(m.assigned_role.id) : '';
    const nextRoleId = roleId == null ? '' : String(roleId);
    if (nextRoleId !== initialRoleId) patch.role_id = nextRoleId === '' ? null : Number(nextRoleId);
    if (!sameOverrides(overrides || {}, m.permission_overrides || {})) {
      patch.permission_overrides = overrides || {};
    }
  }
  return patch;
}

// patch-payload → тело PATCH members/{id}/ бэка: ключ попадает в тело ТОЛЬКО если
// есть в payload (omission-семантика D11 — бэк не трогает не присланные поля);
// role-строка UI конвертится в is_admin.
export function memberPatchBody(payload) {
  const body = {};
  if (payload && payload.role !== undefined) body.is_admin = payload.role === 'admin';
  if (payload && payload.role_id !== undefined) body.role_id = payload.role_id;
  if (payload && payload.permission_overrides !== undefined) {
    body.permission_overrides = payload.permission_overrides;
  }
  return body;
}

function adaptRole(r) {
  return {
    id: r.id,
    name: r.name || '',
    grants: r.grants || {},
    members_count: r.members_count ?? 0,
    restricted_count: countRestrictedKeys(r.grants),
  };
}

// GET roles/ → список ролей доступа ws (адаптированный: + restricted_count).
async function listRoles(workspaceId) {
  const d = await unwrap(teamApi.listRoles(workspaceId));
  return ((d && d.roles) || []).map(adaptRole);
}

// GET members/ → {members, invitations, roles, can_manage}. Бэк 403 не-админу (unwrap бросит
// status=403 → QueryBoundary покажет ошибку). Успех = юзер админ → can_manage=true.
// Роли — отдельная ручка той же волны; её ошибки вкладку НЕ роняют, но сужаем (T5 m3):
//   ТОЛЬКО 404 (бэк без ручки) → roles=[] («Ролей пока нет»); всё прочее, включая
//   сетевые без e.status, → roles=null — маркер «Роли временно недоступны» для TeamTab
//   (уверенное «Ролей пока нет» при живом сбое врало бы админу).
async function getTeam(workspaceId) {
  const d = await unwrap(teamApi.listMembers(workspaceId));
  const roles = await listRoles(workspaceId).catch(
    (e) => ((e && e.status === 404) ? [] : null),
  );
  return {
    members: (d.members || []).map(adaptMember),
    invitations: (d.invitations || []).map(adaptInvitation),
    roles,
    can_manage: true,
  };
}

// POST roles/ {name, grants} → роль (адаптированная). Коды invalid_name/invalid_grants — как есть
// (их разбирает RoleEditorModal).
async function createRole(workspaceId, payload) {
  const d = await unwrap(teamApi.createRole(workspaceId, {
    name: (payload && payload.name) || '',
    grants: (payload && payload.grants) || {},
  }));
  return adaptRole(d.role || d);
}

// PATCH roles/{id}/ {name?, grants?} (grants — ПОЛНАЯ замена) → роль (адаптированная).
async function updateRole(workspaceId, roleId, payload) {
  const body = {};
  if (payload && payload.name !== undefined) body.name = payload.name;
  if (payload && payload.grants !== undefined) body.grants = payload.grants;
  const d = await unwrap(teamApi.updateRole(workspaceId, roleId, body));
  return adaptRole(d.role || d);
}

// DELETE roles/{id}/ → 204 (участники с ролью остаются без роли и получают полный доступ).
async function deleteRole(workspaceId, roleId) {
  return unwrap(teamApi.deleteRole(workspaceId, roleId));
}

// POST invite/ {email, role, role_id?} → invitation-строка (адаптированная). role 'admin' → is_admin:true;
// role_id (int) — назначенная роль доступа, опционально.
// Бэк 409 already_member → remap в code 'exists' (его ждёт InviteMemberModal); invalid_email → 'email'.
async function inviteMember(workspaceId, payload) {
  const body = {
    email: (payload && payload.email) || '',
    is_admin: (payload && payload.role) === 'admin',
  };
  if (payload && payload.role_id != null) body.role_id = payload.role_id;
  try {
    const d = await unwrap(teamApi.inviteMember(workspaceId, body));
    return adaptInvitation(d.invitation || d);
  } catch (e) {
    if (e.code === 'already_member') e.code = 'exists';
    else if (e.code === 'invalid_email') e.code = 'email';
    throw e;
  }
}

// PATCH members/{id}/ → member-патч. EditMemberModal шлёт частичный payload
// (сборка — buildMemberPatch), в тело бэка его переводит memberPatchBody:
//   {role:'admin'|'member'}          → is_admin (шлём ТОЛЬКО при явной смене статуса);
//   {role_id: int|null}              → назначить/снять роль доступа (400 code='invalid_role');
//   {permission_overrides:{key:bool}}→ ПОЛНАЯ замена карты исключений ({} очищает).
async function updateMember(workspaceId, membershipId, payload) {
  const d = await unwrap(teamApi.updateMember(
    workspaceId, membershipId, memberPatchBody(payload),
  ));
  return adaptMember(d.member || d);
}

// DELETE members/{id}/ → {ok}. cannot_remove_owner защищён в UI (protectedRow), но бэк страхует 400.
async function revokeMember(workspaceId, membershipId) {
  return unwrap(teamApi.revokeMember(workspaceId, membershipId));
}

// DELETE invitations/{id}/ → {ok}.
async function revokeInvitation(workspaceId, invitationId) {
  return unwrap(teamApi.revokeInvitation(workspaceId, invitationId));
}

// POST invitations/{id}/resend/ → обновлённая invitation-строка. 409 already_accepted /
// 400 invitation_revoked / 429 rate_limited (коды как есть — TeamTab разбирает их в catch).
async function resendInvitation(workspaceId, invitationId) {
  const d = await unwrap(teamApi.resendInvitation(workspaceId, invitationId));
  return adaptInvitation(d.invitation || d);
}

export const teamReal = {
  getTeam, inviteMember, updateMember, revokeMember, revokeInvitation, resendInvitation,
  listRoles, createRole, updateRole, deleteRole,
};
